import { useEffect, useRef, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, Link } from 'react-router'
import { LogOut, MessageSquare, UserRound } from 'lucide-react'
import { useClientAuthStore } from '../../store/clientAuthStore.js'
import { clearClientAiChatHistory } from '../../utils/clientAiChatStorage.js'
import { ClientNotificationBellWidget } from './ClientNotificationBellWidget.js'

export interface ClientAuthNavWidgetProps {
  loginHref: string
  registerHref: string
  /** Matches the surrounding nav's link styling (color/hover) so the widget blends in when logged out. */
  linkClassName?: string
}

const MENU_ESTIMATED_HEIGHT = 176
const MENU_WIDTH = 256

// Shared across all 3 starter templates' navbars — Hero Gradient mounts it
// directly (structured navbar section), Blue Hero/Template 3 mount it via
// createPortal into a slot inside their custom_html string, since those
// templates are raw HTML with no direct React access.
export default function ClientAuthNavWidget({
  loginHref,
  registerHref,
  linkClassName = 'hover:opacity-75',
}: ClientAuthNavWidgetProps): JSX.Element {
  const user = useClientAuthStore((s) => s.user)
  const clearSession = useClientAuthStore((s) => s.clearSession)
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function handleClickOutside(event: MouseEvent): void {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false)
      }
    }
    function handleClose(): void {
      setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleClose, true)
    window.addEventListener('resize', handleClose)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleClose, true)
      window.removeEventListener('resize', handleClose)
    }
  }, [menuOpen])

  if (!user) {
    return (
      <div className="flex items-center gap-3">
        <a href={loginHref} className={linkClassName} style={{ color: 'inherit' }}>
          Login
        </a>
        <a href={registerHref} className={linkClassName} style={{ color: 'inherit' }}>
          Register
        </a>
      </div>
    )
  }

  const initials = (user.fullName || user.email).charAt(0).toUpperCase()

  const handleToggle = () => {
    if (!menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const openUp = rect.bottom + MENU_ESTIMATED_HEIGHT + 8 > window.innerHeight
      const top = openUp ? rect.top - MENU_ESTIMATED_HEIGHT - 8 : rect.bottom + 8
      setMenuStyle({ top, left: Math.max(8, rect.right - MENU_WIDTH) })
    }
    setMenuOpen((open) => !open)
  }

  const menu = (
    <div
      ref={menuRef}
      className="fixed z-50 w-64 overflow-hidden rounded-[3px] border border-gray-100 bg-white py-1 text-slate-900 shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
      style={menuStyle ?? undefined}
    >
      <div className="px-4 py-3">
        <p className="truncate text-sm font-semibold text-gray-900">{user.fullName || user.email}</p>
        <p className="truncate text-xs text-gray-400 mt-0.5">{user.email}</p>
      </div>
      <div className="mx-3 h-px bg-gray-50" />
      <div className="p-1.5">
        <Link
          to="/client-portal/profile"
          onClick={() => setMenuOpen(false)}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-gray-600 transition-colors hover:bg-gray-50"
        >
          <UserRound className="h-4 w-4" />
          <span>Profile</span>
        </Link>
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false)
            clearSession()
            clearClientAiChatHistory()
            navigate('/client-portal/login')
          }}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  )

  return (
    <div className="flex items-center gap-3">
      <Link
        to="/client-portal/tickets"
        className="inline-flex items-center gap-2 rounded-[3px] px-4 py-2 text-[13px] font-semibold shadow-sm hover:brightness-110 transition-all"
        style={{ backgroundColor: 'var(--portal-accent)', color: 'var(--portal-accent-text, #ffffff)' }}
      >
        <MessageSquare className="h-4 w-4" />
        <span>My Conversation</span>
      </Link>
      <ClientNotificationBellWidget />
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--portal-accent)] text-xs font-semibold text-[var(--portal-accent-text,#ffffff)]"
        aria-label="Account menu"
      >
        {initials}
      </button>

      {menuOpen && menuStyle && createPortal(menu, document.body)}
    </div>
  )
}
